/**
 * KidzioMap — single component every map surface uses.
 *
 * Default adapter: MapLibre GL (open-source).
 * The implementation lives behind this barrel so swapping providers is a
 * 1-file change per PROJECT_BRIEF §17.10.
 */
import { useEffect, useMemo, useRef } from 'react';
// maplibre-gl 6 dropped its default export. A namespace import keeps
// `maplibregl` usable as both a value (`new maplibregl.Map`) and a type
// namespace (`maplibregl.GeoJSONSource`), which is how it's used below.
import * as maplibregl from 'maplibre-gl';
import type { LngLatBoundsLike, StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { getBasemap, VOYAGER_STYLE } from './style';
import type { KidzioMapProps } from './types';
import { cn } from '@/lib/utils';

/** Per-type colour used for the geofence fill, outline, and label.
 *  Picked from the design-token palette so the four place kinds read as
 *  "calm", "school-blue", "work-amber", "generic-pink" — distinct without
 *  shouting. New kinds fall back to `place`. */
const GEOFENCE_PALETTE: Record<string, string> = {
  home: '#10B981',      // emerald-500
  school: '#4F6BFF',    // brand blue
  workplace: '#F59E0B', // amber-500
  place: '#EC4899',     // pink-500
};

export function KidzioMap({
  markers,
  geofences = [],
  center,
  zoom = 13,
  className,
  style,
  basemap = 'voyager',
  onMarkerClick,
  onMapClick,
  onGeofenceClick,
  onGeofenceContextMenu,
  pickMode = false,
  previewCircle = null,
  focusRequest,
  flashGeofence,
  track,
  histories = [],
}: KidzioMapProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  // Single shared hover popup. Re-used across markers so hovering a second
  // marker replaces (rather than stacks) the popup.
  const popupRef = useRef<maplibregl.Popup | null>(null);
  // Track whether we've auto-fit at least once. Subsequent marker updates
  // should NOT re-fit (that resets the user's pan/zoom every time a child moves).
  const didInitialFitRef = useRef(false);
  // Snapshot of the latest markers prop, read by the focus effect WITHOUT being
  // in its dep array — otherwise every WS update would re-trigger easeTo and
  // override the user's manual zoom.
  const latestMarkersRef = useRef(markers);
  latestMarkersRef.current = markers;

  const initialCenter: [number, number] = useMemo(() => {
    if (center) return center;
    if (markers.length > 0 && markers[0]) return [markers[0].lng, markers[0].lat];
    return [-0.1276, 51.5074]; // London fallback
  }, [center, markers]);

  // Initialize map once.
  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const styleToUse: StyleSpecification = (style?.styleJson as StyleSpecification | undefined)
      ?? (getBasemap(basemap) as unknown as StyleSpecification)
      ?? (VOYAGER_STYLE as unknown as StyleSpecification);
    const map = new maplibregl.Map({
      container: ref.current,
      style: style?.styleUrl ?? styleToUse,
      center: initialCenter,
      zoom,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map-click router. Two consumers compete for the same event:
  //   * `onGeofenceClick` — when the click lands on a geofence polygon or
  //     its centroid label. ALWAYS wins — clicking on a circle is a clear
  //     "interact with this place" intent, even if pickMode is armed.
  //   * `onMapClick` — only fires while `pickMode === true` and the click
  //     was on bare map. Used by the "Add place" flow to pick coordinates.
  // Marker (child + pin) clicks bypass this entirely because they're DOM
  // elements with their own listeners.
  //
  // Handlers read through refs so prop changes don't tear down + rebind
  // the map's underlying listener on every render.
  const onMapClickRef = useRef(onMapClick);
  onMapClickRef.current = onMapClick;
  const onGeofenceClickRef = useRef(onGeofenceClick);
  onGeofenceClickRef.current = onGeofenceClick;
  const onGeofenceContextMenuRef = useRef(onGeofenceContextMenu);
  onGeofenceContextMenuRef.current = onGeofenceContextMenu;
  const pickModeRef = useRef(pickMode);
  pickModeRef.current = pickMode;
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const geofenceLayers = () =>
      ['kidzio-geofences-fill', 'kidzio-geofences-label'].filter((id) =>
        map.getLayer(id),
      );
    const featureIdAt = (point: maplibregl.PointLike): string | null => {
      const layers = geofenceLayers();
      if (layers.length === 0) return null;
      const features = map.queryRenderedFeatures(point, { layers });
      const id = features[0]?.properties?.id;
      return typeof id === 'string' ? id : null;
    };

    // Left click — drops a placement pin when pick mode is armed. We
    // deliberately do NOT delete a geofence on plain left-click; that's
    // too easy to mis-fire. Right-click / long-press routes through the
    // contextmenu handler below for an explicit menu.
    const onClick = (e: maplibregl.MapMouseEvent) => {
      // Clicking on a geofence with plain left button → still surface the
      // id to `onGeofenceClick` if the page wants it (e.g. for an info
      // panel later). It just won't trigger destructive actions.
      const id = featureIdAt(e.point);
      if (id) {
        onGeofenceClickRef.current?.(id);
        return;
      }
      if (!pickModeRef.current) return;
      onMapClickRef.current?.({ lng: e.lngLat.lng, lat: e.lngLat.lat });
    };

    // Context menu — right-click on desktop, long-press on touch. Route
    // to `onGeofenceContextMenu` with the screen-space coords so the
    // page can render a floating menu at the cursor.
    const onContextMenu = (e: maplibregl.MapMouseEvent) => {
      const id = featureIdAt(e.point);
      if (!id) return;
      // MapLibre already calls preventDefault on the original event, but
      // be explicit so the browser's native menu never flashes.
      e.preventDefault();
      const orig = e.originalEvent as MouseEvent | TouchEvent;
      let clientX = 0;
      let clientY = 0;
      if ('clientX' in orig) {
        clientX = (orig as MouseEvent).clientX;
        clientY = (orig as MouseEvent).clientY;
      } else if ('touches' in orig && orig.touches.length > 0) {
        const t = orig.touches[0]!;
        clientX = t.clientX;
        clientY = t.clientY;
      }
      onGeofenceContextMenuRef.current?.(id, { clientX, clientY });
    };

    map.on('click', onClick);
    map.on('contextmenu', onContextMenu);
    return () => {
      map.off('click', onClick);
      map.off('contextmenu', onContextMenu);
    };
  }, []);

  // Pointer cursor + info popup on geofence hover. Signals "interactive"
  // and surfaces the name + type so the parent doesn't have to right-click
  // just to see what they're looking at. Same MapLibre Popup machinery
  // the markers use — single shared popup, replaced rather than stacked.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const canvas = map.getCanvas();
    const enter = (e: maplibregl.MapLayerMouseEvent) => {
      if (!pickModeRef.current) canvas.style.cursor = 'pointer';
      const f = e.features?.[0];
      const props = f?.properties as
        | { name?: string; icon?: string; id?: string; subtitle?: string }
        | undefined;
      if (!props?.name) return;
      const icon = props.icon ?? 'place';
      const emoji =
        icon === 'home' ? '🏠' :
        icon === 'school' ? '🏫' :
        icon === 'workplace' ? '💼' : '📍';
      // Prefer the page-supplied subtitle (carries presence info like
      // "2 here · Anna, Ben"); fall back to the type-label hint that
      // tells the parent how to reveal the action menu.
      const subtitle = props.subtitle && props.subtitle.length > 0
        ? props.subtitle
        : `${icon.charAt(0).toUpperCase()}${icon.slice(1)} · right-click for options`;
      // Anchor at the actual cursor lng/lat rather than the centroid so
      // the popup follows the mouse around the polygon.
      popupRef.current?.remove();
      popupRef.current = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 12,
        className: 'kidzio-hover-popup',
      })
        .setLngLat(e.lngLat)
        .setHTML(
          tooltipHtml(
            `${emoji} ${props.name}`,
            subtitle,
            { lat: e.lngLat.lat, lng: e.lngLat.lng },
          ),
        )
        .addTo(map);
    };
    const leave = () => {
      if (!pickModeRef.current) canvas.style.cursor = '';
      popupRef.current?.remove();
      popupRef.current = null;
    };
    const attach = () => {
      for (const id of ['kidzio-geofences-fill', 'kidzio-geofences-label']) {
        if (map.getLayer(id)) {
          map.on('mouseenter', id, enter);
          map.on('mouseleave', id, leave);
        }
      }
    };
    // Layers may not exist yet at first render; re-try once the style
    // finishes loading (also covers basemap swaps).
    if (map.isStyleLoaded()) attach();
    else map.once('load', attach);
    map.on('styledata', attach);
    return () => {
      map.off('styledata', attach);
      for (const id of ['kidzio-geofences-fill', 'kidzio-geofences-label']) {
        map.off('mouseenter', id, enter);
        map.off('mouseleave', id, leave);
      }
    };
  }, []);
  // Crosshair cursor while pick mode is armed. Touches the canvas element
  // directly because MapLibre re-applies its own cursor on every render.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const canvas = map.getCanvas();
    canvas.style.cursor = pickMode ? 'crosshair' : '';
    return () => { canvas.style.cursor = ''; };
  }, [pickMode]);

  // Switch basemap on the fly (keeps markers — they live as separate DOM
  // elements — but the geofence GeoJSON source is part of the style, so
  // re-add it after the new style finishes loading).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || style?.styleUrl || style?.styleJson) return; // explicit style overrides preset
    map.setStyle(getBasemap(basemap) as unknown as StyleSpecification);
  }, [basemap, style?.styleUrl, style?.styleJson]);

  // Sync markers when they change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const seen = new Set<string>();
    for (const m of markers) {
      seen.add(m.id);
      const existing = markersRef.current.get(m.id);
      if (existing) {
        const prev = existing.getLngLat();
        // Pulse the ring on every position change so the parent always
        // gets a visual heartbeat when the child moves — even sub-metre
        // GPS jitter triggers it. Exactly-identical pings (no change at
        // all) still skip, otherwise the marker would re-animate on
        // every React re-render.
        if (prev.lng !== m.lng || prev.lat !== m.lat) {
          pulseMarker(existing.getElement(), m.color ?? '#4F6BFF');
        }
        tweenMarker(existing, [m.lng, m.lat], 900);
        // Update tooltip dataset in-place so hover content stays current
        // (the hover handler reads from dataset, not closure-captured props,
        // so live updates don't need re-binding).
        const el = existing.getElement();
        el.dataset.tooltipTitle = m.tooltip?.title ?? '';
        el.dataset.tooltipSubtitle = m.tooltip?.subtitle ?? '';
        // Sync avatar swap when the parent uploads a photo after the
        // marker first appeared — we keep the same DOM node but swap its
        // background-image / fallback initials. The avatar lives inside
        // the wrapper since the freshness/battery refactor.
        const avatarEl = el.querySelector<HTMLElement>('.kidzio-marker-avatar') ?? el;
        const desired = m.avatarUrl ? `url("${m.avatarUrl}")` : '';
        if (avatarEl.style.backgroundImage !== desired) {
          if (m.avatarUrl) {
            avatarEl.style.backgroundImage = desired;
            avatarEl.style.backgroundSize = 'cover';
            avatarEl.style.backgroundPosition = 'center';
            avatarEl.textContent = '';
          } else {
            avatarEl.style.backgroundImage = '';
            avatarEl.textContent = (m.label ?? '?').slice(0, 2).toUpperCase();
          }
        }
        syncMarkerStatus(el, m.freshness_seconds, m.battery_pct);
        continue;
      }
      const el = renderMarker(
        m.color ?? 'var(--kz-color-brand-primary)',
        m.label ?? '?',
        m.avatarUrl,
        { freshness_seconds: m.freshness_seconds, battery_pct: m.battery_pct },
      );
      el.addEventListener('click', () => onMarkerClick?.(m.id));
      // Tooltip on hover. Reads title/subtitle from the element's dataset
      // so subsequent prop changes (battery %, freshness) reflect immediately.
      el.dataset.tooltipTitle = m.tooltip?.title ?? '';
      el.dataset.tooltipSubtitle = m.tooltip?.subtitle ?? '';
      el.style.cursor = 'pointer';
      // Small grace timer so the user can move from the marker to the
      // popup (and click the "Open in OpenStreetMap" link) without the
      // popup disappearing mid-traversal.
      let popupCloseTimer: ReturnType<typeof setTimeout> | null = null;
      const cancelClose = () => {
        if (popupCloseTimer) { clearTimeout(popupCloseTimer); popupCloseTimer = null; }
      };
      const scheduleClose = () => {
        cancelClose();
        popupCloseTimer = setTimeout(() => {
          popupRef.current?.remove();
          popupRef.current = null;
        }, 250);
      };
      el.addEventListener('mouseenter', () => {
        cancelClose();
        const title = el.dataset.tooltipTitle ?? '';
        const subtitle = el.dataset.tooltipSubtitle ?? '';
        if (!title && !subtitle) return;
        // Anchor at the live marker position so the popup follows tweens.
        const lngLat = markersRef.current.get(m.id)?.getLngLat() ?? { lng: m.lng, lat: m.lat };
        popupRef.current?.remove();
        popupRef.current = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: 22,
          className: 'kidzio-hover-popup',
        })
          .setLngLat([lngLat.lng, lngLat.lat])
          .setHTML(tooltipHtml(title, subtitle, { lat: lngLat.lat, lng: lngLat.lng }))
          .addTo(map);
        // Keep popup alive while the cursor is inside it (so the OSM link
        // stays clickable). MapLibre exposes `_content` as the inner div.
        const popupEl = popupRef.current?.getElement();
        if (popupEl) {
          popupEl.addEventListener('mouseenter', cancelClose);
          popupEl.addEventListener('mouseleave', scheduleClose);
        }
      });
      el.addEventListener('mouseleave', scheduleClose);
      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([m.lng, m.lat])
        .addTo(map);
      markersRef.current.set(m.id, marker);
    }
    // Remove any markers that disappeared.
    for (const [id, marker] of markersRef.current.entries()) {
      if (!seen.has(id)) {
        marker.remove();
        markersRef.current.delete(id);
      }
    }

    // Auto-fit only on the FIRST render where we have markers — never again.
    // Subsequent updates (a child moving) must not yank the user's pan/zoom.
    if (!didInitialFitRef.current && !center && markers.length > 0) {
      didInitialFitRef.current = true;
      if (markers.length === 1 && markers[0]) {
        map.easeTo({ center: [markers[0].lng, markers[0].lat], zoom: Math.max(zoom, 14), duration: 400 });
      } else {
        const lngs = markers.map((m) => m.lng);
        const lats = markers.map((m) => m.lat);
        const bounds: LngLatBoundsLike = [
          [Math.min(...lngs), Math.min(...lats)],
          [Math.max(...lngs), Math.max(...lats)],
        ];
        map.fitBounds(bounds, { padding: 60, duration: 400, maxZoom: 16 });
      }
    }
  }, [markers, center, onMarkerClick, zoom]);

  // Sync geofences as REAL-WORLD polygons. The previous version used a
  // `circle` layer with pixel-based `circle-radius`, which made a 150 m
  // home zone look country-sized at world-zoom. Polygons in lat/lng scale
  // correctly with the map. A symbol layer underneath puts the geofence
  // name + icon on the centroid so the parent knows which is which.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const ready = map.isStyleLoaded() ? Promise.resolve() : new Promise<void>((r) => map.once('load', () => r()));
    void ready.then(() => {
      // Polygon ring per geofence — sized in metres at this latitude.
      const polyFeatures = geofences.map((g) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Polygon' as const,
          coordinates: [circleRing(g.center_lng, g.center_lat, g.radius_m)],
        },
        properties: {
          id: g.id,
          name: g.name,
          icon: g.icon ?? 'place',
          subtitle: g.subtitle ?? '',
          // Colour per type — calm, recognisable. Fill is the same hue at
          // low opacity, outline darker for contrast on satellite basemap.
          color: GEOFENCE_PALETTE[g.icon ?? 'place'] ?? GEOFENCE_PALETTE.place,
        },
      }));
      // Separate "label" features at the centroid so the symbol layer can
      // render the name. MapLibre's SDF glyph atlas doesn't carry color
      // emoji, so we render plain text here — the type signal is already
      // conveyed by the fill/outline colour (and the sidebar's lucide icon).
      const labelFeatures = geofences.map((g) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [g.center_lng, g.center_lat] },
        properties: {
          id: g.id,
          name: g.name,
          icon: g.icon ?? 'place',
          subtitle: g.subtitle ?? '',
          label: g.name,
        },
      }));

      const polySrc = map.getSource('kidzio-geofences') as maplibregl.GeoJSONSource | undefined;
      const labelSrc = map.getSource('kidzio-geofences-labels') as maplibregl.GeoJSONSource | undefined;
      const polyFc = { type: 'FeatureCollection' as const, features: polyFeatures };
      const labelFc = { type: 'FeatureCollection' as const, features: labelFeatures };

      if (polySrc && labelSrc) {
        polySrc.setData(polyFc);
        labelSrc.setData(labelFc);
        return;
      }

      // First-time setup. Wiped + re-added on basemap change.
      if (!polySrc) {
        map.addSource('kidzio-geofences', { type: 'geojson', data: polyFc });
        map.addLayer({
          id: 'kidzio-geofences-fill',
          type: 'fill',
          source: 'kidzio-geofences',
          paint: {
            'fill-color': ['get', 'color'],
            'fill-opacity': 0.14,
          },
        });
        map.addLayer({
          id: 'kidzio-geofences-line',
          type: 'line',
          source: 'kidzio-geofences',
          layout: { 'line-join': 'round' },
          paint: {
            'line-color': ['get', 'color'],
            'line-width': 1.8,
            'line-opacity': 0.6,
          },
        });
      }
      if (!labelSrc) {
        map.addSource('kidzio-geofences-labels', { type: 'geojson', data: labelFc });
        map.addLayer({
          id: 'kidzio-geofences-label',
          type: 'symbol',
          source: 'kidzio-geofences-labels',
          layout: {
            'text-field': ['get', 'label'],
            // Explicit text-font matches the demotiles glyph atlas. Without
            // a font in the atlas, MapLibre falls back to a tofu glyph that
            // renders as a solid black box.
            'text-font': ['Open Sans Regular'],
            'text-size': 11,
            'text-anchor': 'top',
            'text-offset': [0, 0.6],
            'text-allow-overlap': false,
            // Hide the label at world-zoom; it would just clutter. Comes in
            // at zoom 11 (city level) and stays through the rest.
            'text-optional': true,
          },
          paint: {
            'text-color': ['get', 'color'],
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.5,
          },
          minzoom: 11,
        });
      }
    });
    // Re-running on basemap change ensures the geofence source is re-added
    // after `setStyle` wipes it (markers persist as separate DOM elements).
  }, [geofences, basemap]);

  // Geofence flash — when an enter/exit event arrives, briefly highlight
  // the matching polygon. Updates the fill + line layers we just set up so
  // the same paint-expression pattern works against the new lat/lng-based
  // polygon geometry instead of the old pixel-circle paint.
  useEffect(() => {
    if (!flashGeofence) return;
    const map = mapRef.current;
    if (!map) return;
    const fillId = 'kidzio-geofences-fill';
    const lineId = 'kidzio-geofences-line';
    if (!map.getLayer(fillId) || !map.getLayer(lineId)) return;
    const flashId = flashGeofence.id;
    const accent = flashGeofence.kind === 'exited' ? '#F59E0B' : '#22C55E';
    const flashPaint = (intensity: number) => {
      const isFlashing: maplibregl.ExpressionSpecification = ['==', ['get', 'id'], flashId];
      // Fill brightens slightly during flash; non-flashed geofences keep
      // their per-feature `color` property so each type stays recognisable.
      map.setPaintProperty(fillId, 'fill-color', [
        'case', isFlashing, accent, ['get', 'color'],
      ]);
      map.setPaintProperty(fillId, 'fill-opacity', [
        'case', isFlashing, 0.14 + 0.22 * intensity, 0.14,
      ]);
      // Outline pulses thicker and brighter on the flashing fence only.
      map.setPaintProperty(lineId, 'line-color', [
        'case', isFlashing, accent, ['get', 'color'],
      ]);
      map.setPaintProperty(lineId, 'line-width', [
        'case', isFlashing, 1.8 + 4 * intensity, 1.8,
      ]);
      map.setPaintProperty(lineId, 'line-opacity', [
        'case', isFlashing, 0.6 + 0.4 * intensity, 0.6,
      ]);
    };

    const DURATION_MS = 1500;
    const start = performance.now();
    let raf = 0;
    function step(now: number) {
      const t = Math.min(1, (now - start) / DURATION_MS);
      // ease-out: intense → fade
      const intensity = 1 - t * t;
      flashPaint(intensity);
      if (t < 1) raf = requestAnimationFrame(step);
      else flashPaint(0); // explicit clear at end
    }
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [flashGeofence]);

  // Movement history polylines — one GeoJSON source + line layer per
  // child. We track which ids we've added so toggling a child off
  // removes only that source, not the others. The source/layer ids are
  // namespaced by child id so they survive basemap swaps cleanly.
  const historyIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const ready = map.isStyleLoaded()
      ? Promise.resolve()
      : new Promise<void>((r) => map.once('load', () => r()));
    void ready.then(() => {
      const visible = new Set(histories.map((h) => h.id));
      // Drop sources/layers for histories no longer in the prop.
      for (const id of historyIdsRef.current) {
        if (!visible.has(id)) {
          const layerId = `kidzio-history-${id}-line`;
          const sourceId = `kidzio-history-${id}`;
          try {
            if (map.getLayer(layerId)) map.removeLayer(layerId);
            if (map.getSource(sourceId)) map.removeSource(sourceId);
          } catch { /* basemap mid-swap; rerun on next tick */ }
          historyIdsRef.current.delete(id);
        }
      }
      // Add or update each visible history.
      for (const h of histories) {
        const sourceId = `kidzio-history-${h.id}`;
        const layerId = `kidzio-history-${h.id}-line`;
        const data = {
          type: 'Feature' as const,
          geometry: { type: 'LineString' as const, coordinates: h.coords },
          properties: {},
        };
        const existing = map.getSource(sourceId) as
          | maplibregl.GeoJSONSource
          | undefined;
        if (existing) {
          existing.setData(data);
        } else {
          map.addSource(sourceId, { type: 'geojson', data });
          map.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': h.color,
              'line-width': 3,
              'line-opacity': 0.75,
            },
          });
          historyIdsRef.current.add(h.id);
        }
      }
    });
  }, [histories, basemap]);

  // Track line — straight-line path between two coordinates. Rendered as a
  // GeoJSON LineString source + layer so it composes with the basemap and
  // survives style switches via the same re-add-on-style-load pattern as
  // the geofence layer. We auto-fit the camera to the track on first
  // appearance so the parent immediately sees both endpoints.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const SOURCE_ID = 'kidzio-track';
    const LAYER_GLOW_ID = 'kidzio-track-glow';
    const LAYER_LINE_ID = 'kidzio-track-line';

    const removeTrack = () => {
      for (const id of [LAYER_LINE_ID, LAYER_GLOW_ID]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    };

    if (!track) {
      // Best-effort cleanup. Wrapped because map.removeSource throws if
      // called while a style transition is mid-flight.
      try { removeTrack(); } catch { /* ignore */ }
      return;
    }

    const ready = map.isStyleLoaded()
      ? Promise.resolve()
      : new Promise<void>((r) => map.once('load', () => r()));
    void ready.then(() => {
      const color = track.color ?? '#f43f5e'; // rose-500 default (panic palette)
      // Use the OSRM road-network polyline when we have it; otherwise fall
      // back to a straight from→to so the visualization never blanks out
      // if routing is unreachable.
      const lineCoords: [number, number][] =
        track.coords && track.coords.length >= 2
          ? track.coords
          : [track.from, track.to];
      const data = {
        type: 'Feature' as const,
        geometry: {
          type: 'LineString' as const,
          coordinates: lineCoords,
        },
        properties: {},
      };
      const src = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
      if (src) {
        src.setData(data);
      } else {
        map.addSource(SOURCE_ID, { type: 'geojson', data });
        // Two layers: a wider soft glow + a sharp dashed line on top, so it
        // reads clearly against any basemap (light cartographic or dark
        // satellite) without obscuring the markers.
        map.addLayer({
          id: LAYER_GLOW_ID,
          type: 'line',
          source: SOURCE_ID,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': color,
            'line-width': 10,
            'line-opacity': 0.22,
          },
        });
        map.addLayer({
          id: LAYER_LINE_ID,
          type: 'line',
          source: SOURCE_ID,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': color,
            'line-width': 3,
            'line-dasharray': [2, 1.5],
          },
        });
      }

      // Fit camera to the two endpoints (with padding) so the parent sees
      // the full route — but only once, so a track update from a moving
      // child doesn't yank the camera every WS tick.
      if (!didInitialFitRef.current) {
        const lngs = [track.from[0], track.to[0]];
        const lats = [track.from[1], track.to[1]];
        map.fitBounds(
          [
            [Math.min(...lngs), Math.min(...lats)],
            [Math.max(...lngs), Math.max(...lats)],
          ],
          { padding: 100, duration: 600, maxZoom: 15 },
        );
        didInitialFitRef.current = true;
      }
    });
    // Re-run on basemap change because setStyle wipes the source/layers,
    // same pattern as the geofence effect uses.
  }, [track, basemap]);

  // Focus request — fly camera to explicit coords OR to a marker (by id).
  // Coordinates win when both are present so historical events (e.g. an old
  // SOS lat/lng from SosPage) don't get hijacked by the child's current pin.
  // IMPORTANT: dep on `focusRequest` only (not `markers`). Otherwise every
  // WS-driven marker update would re-fire easeTo and yank the user's zoom.
  // We read the latest markers via a ref so the by-id lookup still works.
  useEffect(() => {
    if (!focusRequest) return;
    const map = mapRef.current;
    if (!map) return;
    // fitAll wins over id/lat/lng — re-fit the viewport around every current
    // marker so the parent can pull back to "see everyone" from anywhere.
    if (focusRequest.fitAll) {
      const ms = latestMarkersRef.current;
      if (ms.length === 0) return;
      if (ms.length === 1 && ms[0]) {
        map.easeTo({ center: [ms[0].lng, ms[0].lat], zoom: 14, duration: 700, essential: true });
      } else {
        const lngs = ms.map((m) => m.lng);
        const lats = ms.map((m) => m.lat);
        map.fitBounds(
          [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
          { padding: 80, duration: 700, maxZoom: 15 },
        );
      }
      didInitialFitRef.current = true;
      return;
    }
    let center: [number, number] | null = null;
    if (typeof focusRequest.lat === 'number' && typeof focusRequest.lng === 'number') {
      center = [focusRequest.lng, focusRequest.lat];
    } else if (focusRequest.id) {
      const target = latestMarkersRef.current.find((m) => m.id === focusRequest.id);
      if (target) center = [target.lng, target.lat];
    }
    if (!center) return;
    map.easeTo({
      center,
      zoom: focusRequest.zoom ?? 16,
      duration: 700,
      essential: true,
    });
    // Mark "didInitialFit" so the normal auto-fit doesn't overwrite this focus.
    didInitialFitRef.current = true;
  }, [focusRequest]);

  // Preview ring — a translucent ghost of the geofence following the cursor
  // while the parent is in pick mode (currently used by "Move place" so they
  // can see WHERE the new radius will land before committing).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!previewCircle) return;

    const SRC = 'kidzio-preview-circle';
    const FILL = 'kidzio-preview-circle-fill';
    const LINE = 'kidzio-preview-circle-line';
    const color = previewCircle.color ?? '#4F6BFF';
    const radius = previewCircle.radius_m;
    const emptyFc = { type: 'FeatureCollection' as const, features: [] };

    let detach: (() => void) | undefined;
    let removed = false;

    const ready = map.isStyleLoaded()
      ? Promise.resolve()
      : new Promise<void>((r) => map.once('load', () => r()));

    void ready.then(() => {
      if (removed) return;
      if (!map.getSource(SRC)) {
        map.addSource(SRC, { type: 'geojson', data: emptyFc });
        map.addLayer({
          id: FILL,
          type: 'fill',
          source: SRC,
          paint: { 'fill-color': color, 'fill-opacity': 0.18 },
        });
        map.addLayer({
          id: LINE,
          type: 'line',
          source: SRC,
          layout: { 'line-join': 'round' },
          paint: {
            'line-color': color,
            'line-width': 2,
            'line-dasharray': [2, 2],
            'line-opacity': 0.85,
          },
        });
      }
      const src = map.getSource(SRC) as maplibregl.GeoJSONSource;
      const onMove = (e: maplibregl.MapMouseEvent) => {
        src.setData({
          type: 'FeatureCollection' as const,
          features: [{
            type: 'Feature' as const,
            geometry: {
              type: 'Polygon' as const,
              coordinates: [circleRing(e.lngLat.lng, e.lngLat.lat, radius)],
            },
            properties: {},
          }],
        });
      };
      const onLeave = () => src.setData(emptyFc);
      map.on('mousemove', onMove);
      map.on('mouseout', onLeave);
      detach = () => {
        map.off('mousemove', onMove);
        map.off('mouseout', onLeave);
      };
    });

    return () => {
      removed = true;
      detach?.();
      if (map.getLayer(FILL)) map.removeLayer(FILL);
      if (map.getLayer(LINE)) map.removeLayer(LINE);
      if (map.getSource(SRC)) map.removeSource(SRC);
    };
  }, [previewCircle?.radius_m, previewCircle?.color, previewCircle]);

  return <div ref={ref} className={cn('h-full w-full rounded-card overflow-hidden', className)} />;
}

/** Build a polygon ring approximating a circle of `radiusMeters` around
 *  (centerLng, centerLat). Returns `[lng, lat]` pairs with the ring closed.
 *
 *  Done in lat/lng so MapLibre renders it as a real-world shape that
 *  scales with zoom — the alternative (a `circle` layer with pixel
 *  `circle-radius`) covers entire countries at world-zoom because pixel
 *  size is zoom-independent. */
function circleRing(
  centerLng: number,
  centerLat: number,
  radiusMeters: number,
  points = 64,
): [number, number][] {
  // Metres per degree at this latitude — the longitude factor shrinks
  // toward the poles, so a circle near a polar latitude becomes an oval
  // (correct: equal real-world distances aren't equal in degrees).
  const metersPerDegLat = 111_320;
  const metersPerDegLng = 111_320 * Math.cos((centerLat * Math.PI) / 180) || 1;
  const ring: [number, number][] = [];
  for (let i = 0; i < points; i++) {
    const theta = (i / points) * 2 * Math.PI;
    const dx = radiusMeters * Math.cos(theta);
    const dy = radiusMeters * Math.sin(theta);
    ring.push([
      centerLng + dx / metersPerDegLng,
      centerLat + dy / metersPerDegLat,
    ]);
  }
  // Close the ring — GeoJSON polygons require first == last.
  ring.push(ring[0] as [number, number]);
  return ring;
}

/** HTML-escape user-supplied strings before injecting via setHTML. Marker
 *  titles can be child names, free-form parent labels, etc. — all untrusted. */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' :
    c === '<' ? '&lt;' :
    c === '>' ? '&gt;' :
    c === '"' ? '&quot;' :
    '&#39;',
  );
}

/** Compose the inner HTML for a hover popup. Inline-styled so we don't have
 *  to ship dedicated CSS for one widget. When lat/lng are passed, appends
 *  a small "Open in OpenStreetMap" link so the parent can deep-link to the
 *  external map for directions / sharing. */
function tooltipHtml(title: string, subtitle: string, coords?: { lat: number; lng: number }): string {
  const sub = subtitle
    ? `<div style="margin-top:2px;font-size:11px;line-height:1.3;opacity:0.75;">${escapeHtml(subtitle)}</div>`
    : '';
  const osm = coords
    ? `<a href="https://www.openstreetmap.org/?mlat=${coords.lat}&mlon=${coords.lng}&zoom=16"` +
      ` target="_blank" rel="noopener noreferrer"` +
      ` style="display:inline-flex;align-items:center;gap:4px;margin-top:6px;font-size:10px;font-weight:600;` +
      `color:#4F6BFF;text-decoration:none;">` +
      `<span style="font-size:11px;">📍</span> Open in OpenStreetMap` +
      `<span style="font-size:9px;opacity:0.7;">↗</span></a>`
    : '';
  return (
    `<div style="font-size:12px;font-weight:600;line-height:1.3;color:#0F172A;` +
    `padding:2px 4px;min-width:80px;">` +
    `${escapeHtml(title)}${sub}${osm}</div>`
  );
}

/** Pulse a coloured halo ring around the marker for ~1.5 seconds when the
 *  child moves. Uses the Web Animations API directly on the DOM element
 *  — no global keyframes, no class management, automatically GC'd when
 *  the marker is removed. Two pulses so a single movement is hard to
 *  miss; ring colour echoes the child's identity colour. */
function pulseMarker(el: HTMLElement, color: string): void {
  // Target the inner round `.kidzio-marker-avatar` so the box-shadow ring
  // follows its border-radius. Pulsing the wrapper would draw a square
  // halo since the wrapper has no border-radius (it's just a positioning
  // shell for the freshness dot + battery badge).
  const target = el.querySelector<HTMLElement>('.kidzio-marker-avatar') ?? el;
  target.animate(
    [
      { boxShadow: `0 0 0 0 ${color}66, 0 10px 15px -3px rgba(0,0,0,0.15)` },
      { boxShadow: `0 0 0 14px ${color}00, 0 10px 15px -3px rgba(0,0,0,0.15)` },
      { boxShadow: `0 0 0 0 ${color}66, 0 10px 15px -3px rgba(0,0,0,0.15)` },
      { boxShadow: `0 0 0 14px ${color}00, 0 10px 15px -3px rgba(0,0,0,0.15)` },
    ],
    { duration: 1500, iterations: 1, easing: 'ease-out' },
  );
}

function renderMarker(
  color: string,
  label: string,
  avatarUrl?: string,
  opts?: { freshness_seconds?: number; battery_pct?: number },
): HTMLDivElement {
  // Outer wrap provides positioning context for the freshness dot + low-
  // battery badge. The colored ring still belongs to the inner `.avatar`
  // node so it keeps reading as "this child" rather than as a status hue.
  const wrap = document.createElement('div');
  wrap.className = 'kidzio-marker relative -translate-y-1 h-14 w-14';

  const avatar = document.createElement('div');
  avatar.className =
    'kidzio-marker-avatar flex h-full w-full cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-white text-sm font-semibold text-white shadow-lg ring-1 ring-black/10';
  avatar.style.backgroundColor = color;
  if (avatarUrl) {
    // The colored ring stays as a fallback while the image loads (and shows
    // through if the photo has transparency / fails to load), so the parent
    // can still identify the child by colour at a glance.
    avatar.style.backgroundImage = `url("${avatarUrl}")`;
    avatar.style.backgroundSize = 'cover';
    avatar.style.backgroundPosition = 'center';
  } else {
    avatar.textContent = label.slice(0, 2).toUpperCase();
  }
  wrap.appendChild(avatar);

  // Freshness dot — sits on the bottom-right corner. Mirrors the sidebar
  // FreshnessPill colors (emerald / amber / rose).
  const freshDot = document.createElement('span');
  freshDot.className =
    'kidzio-marker-fresh absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white shadow-sm';
  wrap.appendChild(freshDot);

  // Low-battery badge — bottom-left corner. Hidden by default; shown when
  // syncMarkerStatus is called with battery < 20.
  const batteryBadge = document.createElement('span');
  batteryBadge.className =
    'kidzio-marker-battery absolute -bottom-1 -left-1 hidden h-4 w-4 items-center justify-center rounded-full border-2 border-white bg-rose-600 text-[9px] font-bold leading-none text-white shadow-sm';
  batteryBadge.textContent = '!';
  wrap.appendChild(batteryBadge);

  syncMarkerStatus(wrap, opts?.freshness_seconds, opts?.battery_pct);
  return wrap;
}

/** Update the freshness dot + low-battery badge on an existing marker
 *  element. Used both at create time and when WS updates re-render. */
function syncMarkerStatus(
  wrap: HTMLElement,
  freshnessSeconds?: number,
  batteryPct?: number,
): void {
  const freshDot = wrap.querySelector<HTMLElement>('.kidzio-marker-fresh');
  if (freshDot) {
    let bg = '#10b981'; // emerald — live
    let title = 'Live';
    if (freshnessSeconds !== undefined) {
      if (freshnessSeconds >= 300) {
        bg = '#f43f5e'; // rose — stale > 5 min
        title = `Stale · ${Math.round(freshnessSeconds / 60)}m ago`;
      } else if (freshnessSeconds >= 60) {
        bg = '#f59e0b'; // amber — 1–5 min
        title = `${Math.round(freshnessSeconds / 60)}m ago`;
      }
    }
    freshDot.style.backgroundColor = bg;
    freshDot.title = title;
  }
  const batteryBadge = wrap.querySelector<HTMLElement>('.kidzio-marker-battery');
  if (batteryBadge) {
    const low = typeof batteryPct === 'number' && batteryPct < 20;
    if (low) {
      batteryBadge.classList.remove('hidden');
      batteryBadge.classList.add('flex');
      batteryBadge.title = `Low battery · ${batteryPct}%`;
    } else {
      batteryBadge.classList.add('hidden');
      batteryBadge.classList.remove('flex');
    }
  }
}

// Per-marker animation handle stored on the marker instance to cancel mid-flight.
const tweenHandles = new WeakMap<maplibregl.Marker, number>();

/** Animate a marker from its current position to `target` over `durationMs` (ease-out). */
function tweenMarker(marker: maplibregl.Marker, target: [number, number], durationMs: number): void {
  // Cancel any in-flight tween for this marker so updates aren't fighting each other.
  const prev = tweenHandles.get(marker);
  if (prev !== undefined) cancelAnimationFrame(prev);

  const start = marker.getLngLat();
  const fromLng = start.lng;
  const fromLat = start.lat;
  const dLng = target[0] - fromLng;
  const dLat = target[1] - fromLat;
  // Skip animation for trivially small moves (avoids needless RAFs at idle).
  if (Math.abs(dLng) < 1e-7 && Math.abs(dLat) < 1e-7) {
    marker.setLngLat(target);
    return;
  }
  const t0 = performance.now();

  function step(now: number) {
    const t = Math.min(1, (now - t0) / durationMs);
    // ease-out cubic
    const eased = 1 - (1 - t) ** 3;
    marker.setLngLat([fromLng + dLng * eased, fromLat + dLat * eased]);
    if (t < 1) {
      tweenHandles.set(marker, requestAnimationFrame(step));
    } else {
      tweenHandles.delete(marker);
    }
  }
  tweenHandles.set(marker, requestAnimationFrame(step));
}
